import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { X, Lock, Star, TrendingUp, BarChart3, Calculator, CalendarDays, PieChart, Wallet, Target, Share2, FileSpreadsheet, Briefcase, Receipt, BookOpen, FileText, Upload } from 'lucide-react';
import ModalCloseButton from '@/components/ModalCloseButton';
import PasswordInput from '@/components/PasswordInput';
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter';
import { validatePassword } from '@/lib/passwordRules';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  return String(detail);
}

const FEATURES = [
  { icons: [Star, Briefcase], text: 'Watchlist & Portfolio — track holdings & gains' },
  { icon: Upload,         text: 'CSV import — builds positions, cash flow, lots' },
  { icon: Receipt,        text: 'Tax-Lot Optimizer — harvestable losses, wash-sale alerts' },
  { icon: FileText,       text: 'Year-End Snapshot & Tax-Harvest reports — printable' },
  { icon: BookOpen,       text: 'Investing Journal — thesis on add/sell, weekly review' },
  { icon: Target,         text: 'Monthly Income Goal — set a target & track progress' },
  { icon: Calculator,     text: 'DRIP Calculator — Per ticker and portfolio' },
  { icon: BarChart3,      text: 'Yield History & Time Machine — spot trends & risks' },
  { icon: CalendarDays,   text: 'Dividend Calendar — upcoming ex-div & pay dates' },
  { icon: PieChart,       text: 'Sector Rebalancer — plan buys & sells by target %' },
  { icon: Wallet,         text: 'Cash Tracking — deposits, sells, full transaction history' },
  { icon: FileSpreadsheet,text: 'Personalised Excel / Sheets workbook — 9-tab download' },
  { icon: Share2,         text: 'My Share Page — public read-only portfolio link' },
];

export default function LoginModal({ open, onClose }) {
  // Feb 2026 — Two-button design. Sign-Up is now a dedicated flow:
  // first click reveals the Name field with a red-glow prompt, second
  // click validates + creates the account. No more Sign-Up/Log-In
  // toggle at the bottom of the modal.
  const [showNameField, setShowNameField] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState({ name: false, email: false, password: false });
  const nameToastIdRef = useRef(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  if (!open) return null;

  const clearFieldError = (k) => setFieldError((prev) => (prev[k] ? { ...prev, [k]: false } : prev));

  const showNameToastOnce = () => {
    if (nameToastIdRef.current) toast.dismiss(nameToastIdRef.current);
    const id = toast.info('Please add your name or Nickname', { duration: 5000 });
    nameToastIdRef.current = id;
    // Dismiss on any click OR keydown — matches the "click anywhere or
    // start typing" spec. Delayed 50ms so the click that spawned the
    // toast doesn't immediately cancel it.
    const dismiss = () => {
      toast.dismiss(id);
      window.removeEventListener('click', dismiss);
      window.removeEventListener('keydown', dismiss);
    };
    setTimeout(() => {
      window.addEventListener('click', dismiss, { once: true });
      window.addEventListener('keydown', dismiss, { once: true });
    }, 50);
  };

  const attemptLogin = async () => {
    setError('');
    const errs = { name: false, email: false, password: false };
    if (!email.trim()) errs.email = true;
    if (!password) errs.password = true;
    if (errs.email || errs.password) {
      setFieldError(errs);
      toast.error('Please fill in your email and password.', { duration: 5000 });
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!', { description: "You're signed in." });
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const attemptSignup = async () => {
    setError('');
    // Stage 1 — first Sign-Up click: reveal the Name field with a
    // red-glow prompt so the user knows what's missing. Button label
    // flips to "Create Account" for the second click.
    if (!showNameField) {
      setShowNameField(true);
      setFieldError({ name: true, email: false, password: false });
      showNameToastOnce();
      return;
    }
    // Stage 2 — validate all three fields and register.
    const errs = { name: false, email: false, password: false };
    let msg = null;
    if (!name.trim()) { errs.name = true; msg = 'Please add your name or Nickname.'; }
    if (!email.trim()) { errs.email = true; msg = msg || 'Please enter your email.'; }
    if (!validatePassword(password).valid) {
      errs.password = true;
      msg = msg || 'Password must be 8+ chars with 1 uppercase, 1 lowercase, and 1 number.';
    }
    if (errs.name || errs.email || errs.password) {
      setFieldError(errs);
      toast.error(msg, { duration: 5000 });
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('Welcome to DiviDrip!', { description: 'Your account is ready to go.' });
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    // Enter-key default: treat as Log In unless the Name field is
    // showing (Sign-Up flow in progress), then treat as Sign-Up submit.
    e.preventDefault();
    if (showNameField) return attemptSignup();
    return attemptLogin();
  };

  const handleGoogleLogin = () => {
    // Round 86c (May 2026) — If a "post-login target" hint was set in
    // sessionStorage just before this modal opened (e.g., by the Today
    // tab's "Sign in" chip or the Stock Modal's My Portfolio Sign-Up
    // button), honour it so the user lands back on the exact stock
    // modal + tab they were looking at. Otherwise default to root.
    let target = window.location.origin + '/';
    try {
      const hint = sessionStorage.getItem('post_login_redirect');
      if (hint) {
        // Validate it's same-origin to prevent open-redirect abuse.
        const u = new URL(hint, window.location.origin);
        if (u.origin === window.location.origin) target = u.toString();
        sessionStorage.removeItem('post_login_redirect');
      }
    } catch (e) { /* ignore */ }
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(target)}`;
  };

  // Round 97l (Feb 2026) — Portal to document.body so the modal escapes
  // any parent component's pointer-events trap. Without this, when the
  // LoginModal opens from inside an already-open Radix Dialog (such as
  // the CompareModal's "Pin Compare" button when the user is logged
  // out), Radix\'s overlay catches every click and the LoginModal looks
  // like a non-interactive picture even though it\'s visually on top.
  //
  // Radix Dialog also injects `pointer-events: none` directly on the
  // <body> element via inline style — that style cascades into our
  // portaled child unless we explicitly opt back in with
  // `pointer-events: auto` on the overlay AND content. Without that
  // override the modal renders but clicks fall through to the
  // underlying Dialog, which is exactly what the user reported (the X
  // they clicked landed on the ex-div alert\'s X behind the modal).
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      style={{ pointerEvents: 'auto' }}
      onClick={onClose}
      data-testid="login-modal-overlay"
    >
      <div
        className="w-full max-w-sm bg-background rounded-xl p-6 shadow-2xl animate-fade-in-up relative"
        style={{ pointerEvents: 'auto' }}
        onClick={e => e.stopPropagation()}
        data-testid="login-modal"
      >
        <ModalCloseButton onClick={onClose} testId="login-modal-close" className="absolute top-3 right-3" />

        {/* Header */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
            <Lock className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Chivo' }}>Sign up or Log in to unlock</h2>
          <p className="text-xs text-muted-foreground mt-1">Create a free account and add positions to your Portfolio to access all these features:</p>
        </div>

        {/* Feature list */}
        <div className="space-y-1.5 mb-5">
          {FEATURES.map((f, i) => {
            const icons = f.icons || [f.icon];
            return (
              <div key={i} className="flex items-center gap-2.5 text-[12px] leading-tight">
                <span className="inline-flex items-center gap-1 shrink-0">
                  {icons.map((Icon, j) => (
                    <Icon key={j} className="w-4 h-4 text-primary" />
                  ))}
                </span>
                <span>{f.text}</span>
              </div>
            );
          })}
        </div>

        {/* Auth form */}
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="login-modal-form">
          {showNameField && (
            <Input
              data-testid="login-modal-name"
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={e => { setName(e.target.value); clearFieldError('name'); }}
              className={`h-10 transition-shadow ${fieldError.name ? 'ring-2 ring-rose-500 shadow-[0_0_12px_rgba(244,63,94,0.55)] border-rose-500' : ''}`}
              required
              autoFocus
            />
          )}
          <Input
            data-testid="login-modal-email"
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => { setEmail(e.target.value); clearFieldError('email'); }}
            className={`h-10 transition-shadow ${fieldError.email ? 'ring-2 ring-rose-500 shadow-[0_0_12px_rgba(244,63,94,0.55)] border-rose-500' : ''}`}
            required
          />
          <PasswordInput
            data-testid="login-modal-password"
            testIdBase="login-modal-password"
            placeholder="Password"
            value={password}
            onChange={e => { setPassword(e.target.value); clearFieldError('password'); }}
            className={`h-10 transition-shadow ${fieldError.password ? 'ring-2 ring-rose-500 shadow-[0_0_12px_rgba(244,63,94,0.55)] border-rose-500' : ''}`}
            required
          />
          {showNameField && <PasswordStrengthMeter password={password} />}
          {!showNameField && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { onClose && onClose(); navigate('/forgot-password'); }}
                className="text-[11px] text-primary hover:underline font-medium"
                data-testid="login-modal-forgot-password"
              >
                Forgot password?
              </button>
            </div>
          )}
          {error && <p className="text-xs font-medium text-rose-600 dark:text-rose-300" data-testid="login-modal-error">{error}</p>}
          {/* Two-button row — Log In (left, primary blue) + Sign-Up
              (right, manilla yellow). Feb 2026: replaces the single
              "Sign Up / Log In — It's Free" combined button. */}
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={attemptLogin}
              disabled={loading}
              className="flex-1 h-10 font-semibold"
              data-testid="login-modal-login-btn"
            >
              Log In
            </Button>
            <Button
              type="button"
              onClick={attemptSignup}
              disabled={loading}
              className="flex-1 h-10 font-semibold text-slate-900 bg-[#E7C9A4] hover:bg-[#d4b088] focus-visible:ring-[#c9a377]"
              data-testid="login-modal-signup-btn"
            >
              {loading ? 'Please wait...' : showNameField ? 'Create Account' : "Sign-Up It's Free"}
            </Button>
          </div>
        </form>

        {/* Divider */}
        <div className="relative my-3">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">or</span>
          </div>
        </div>

        {/* Google — Feb 2026: icon + text bumped 1.5x per user spec so
            OAuth entry point is more prominent. Aug 2026: colors flipped
            to match Google branding guidelines — white button on dark
            theme, dark button on light theme. */}
        <Button
          variant="outline"
          className="w-full h-14 text-base font-semibold bg-slate-900 text-white border-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:border-white dark:hover:bg-slate-100"
          onClick={handleGoogleLogin}
          data-testid="login-modal-google"
        >
          <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>

        {/* Sep 2026 — Users testing the site were alarmed when the
            Google flow left twylightcrow.com and showed
            auth.emergentagent.com (one thought the site was being
            hacked). Say so up front, in plain words, where they are
            about to click. Email/password never leaves the domain, so
            this sits under the Google button only. */}
        <p
          className="text-[11px] text-muted-foreground text-center mt-2 leading-snug"
          data-testid="login-modal-oauth-note"
        >
          Google signs in via <span className="font-semibold">emergentagent.com</span> - our database provider
        </p>

        {/* Feb 2026 — "Don't have an account? Create Account" toggle
            removed. Sign-Up is now its own top-level button, no
            secondary toggle needed. */}
        <p className="text-[10px] font-bold text-slate-700 dark:text-slate-200 text-center mt-3">
          Dividend Info is always free — no account needed.
        </p>
      </div>
    </div>,
    document.body
  );
}
